import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, useApi } from '@/api/client';
import { StatusPill } from '@/components/StatusPill';
import { ScheduleEditor } from '@/components/ScheduleEditor';
import { RecipientsEditor } from '@/components/RecipientsEditor';
import { RecipientConfirmDialog, RecipientMismatchNotice } from '@/components/RecipientWarning';
import { useRecipientCheck } from '@/hooks/useRecipientCheck';
import { ParametersEditor, SubjectPlaceholderHint } from '@/components/ParametersEditor';
import { PickupDriftNotice, PickupToggle } from '@/components/PickupToggle';
import { describeDelivery } from '@/components/pickupFormat';
import { ReassignOwnerDialog } from '@/components/ReassignOwnerDialog';
import { subscriptionTitle } from '@/types';
import type { DirectoryUser, RecipientCheckResult, SubscriptionDetail, UpdateSubscriptionRequest } from '@/types';

/**
 * What the create form hands over when the subscription was made but its pickup route was not.
 *
 * Carried in router state rather than the URL because it describes one moment — the create that
 * just happened — and not this subscription's standing condition; a reload should not keep
 * reporting it, and a pasted link should not report it to somebody else. The lasting version of
 * the same fact is PickupDriftNotice, which reads the report server.
 */
type CreatePickupProblem = { outcome: string; problem?: string | null };

/**
 * PBIRS rendering-extension ids, not file extensions. EXCELOPENXML is what almost every
 * subscription on this server uses; MHTML is the "embed the report in the email body" option,
 * which is the second most common and had no way to be chosen here.
 */
const RENDER_FORMATS = [
  { value: 'EXCELOPENXML', label: 'Excel (.xlsx)' },
  { value: 'PDF', label: 'PDF' },
  { value: 'WORDOPENXML', label: 'Word (.docx)' },
  { value: 'CSV', label: 'CSV' },
  { value: 'MHTML', label: 'Web archive (MHTML)' },
  { value: 'XML', label: 'XML' },
  { value: 'IMAGE', label: 'Image (TIFF)' }
];

/**
 * Sizes the comment textarea to its content instead of a fixed `rows`, which used to clip the
 * default body text ("Please find the requested report attached. / Kind regards / Key Travel
 * Team") behind an inner scrollbar the moment the page loaded.
 *
 * CSS-first (`field-sizing: content`) rather than a scrollHeight-measuring hook: this project has
 * no existing autosize pattern to stay consistent with, and the browser doing the measurement
 * means there is no JS-held height to go stale when `value` changes from outside — a form reset
 * included, since that just swaps the controlled value and the box re-measures on the next paint
 * exactly as it would for typing. `rows` is kept as the pre-`field-sizing` fallback size only.
 */
const AUTOSIZE_TEXTAREA_STYLE: CSSProperties = {
  fieldSizing: 'content',
  minHeight: '8em',
  maxHeight: '28em',
  overflowY: 'auto'
};

/**
 * How many runs this page asks for. /history is where the whole log lives; this section exists to
 * answer "did last night's run work, and why not?" without leaving the subscription, and an hourly
 * subscription would otherwise bury the rest of the page under a fortnight of identical successes.
 */
const RUN_HISTORY_TAKE = 15;

/**
 * How long the history keeps re-reading itself after a run is triggered, and how often.
 *
 * There is nothing to wait for synchronously: run-now is answered 202 the moment the report server
 * accepts the request — the same fact the bulk-run panel on My Subscriptions says out loud — and
 * nothing calls back when the report has actually rendered and the mail has left. So the choice is
 * between a spinner that can never end and asking again for a while, and this is the second. 10s
 * matches /history's own cadence; two minutes is long enough for a small report to land and short
 * enough that a page somebody walked away from stops talking to the API.
 */
const RUN_WATCH_WINDOW_MS = 120_000;
const RUN_WATCH_POLL_MS = 10_000;

export function SubscriptionDetailPage() {
  const { id = '' } = useParams();
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['sub', id],
    queryFn: () => api.getSubscription(id),
    enabled: Boolean(id)
  });

  // Already fetched once at the app shell and shared through the query cache — this does not add
  // a network round trip, it just reads whether the caller may reassign ownership from here.
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });

  const [draft, setDraft] = useState<SubscriptionDetail | null>(null);
  useEffect(() => { if (data) setDraft(data); }, [data]);

  // Read once, at the first render after the create navigated here. Dismissible because it is
  // news, not a condition — the pickup panel below is where the state of the route actually lives.
  const location = useLocation();
  const [createPickupProblem, setCreatePickupProblem] = useState<CreatePickupProblem | null>(
    () => (location.state as { pickupSetupProblem?: CreatePickupProblem } | null)?.pickupSetupProblem ?? null
  );

  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignedTo, setReassignedTo] = useState<string | null>(null);
  const reassign = useMutation({
    mutationFn: (user: DirectoryUser) => api.reassignOwner(id, user.objectId, user.displayName),
    onSuccess: (_result, user) => {
      setReassignOpen(false);
      setReassignedTo(user.displayName);
      qc.invalidateQueries({ queryKey: ['sub', id] });
    }
  });

  // Set when a save came back refused for an unconfirmed recipient mismatch. Holding the API's own
  // answer rather than the inline one guarantees the dialog shows exactly what the server objected
  // to, and carries the fingerprint that the confirmation has to echo back.
  const [pendingConfirm, setPendingConfirm] = useState<RecipientCheckResult | null>(null);

  const save = useMutation({
    mutationFn: (req: UpdateSubscriptionRequest) => api.updateSubscription(id, req),
    onSuccess: () => {
      setPendingConfirm(null);
      qc.invalidateQueries({ queryKey: ['sub', id] });
    },
    onError: (e: Error) => {
      if (e instanceof ApiError && e.code === 'recipient.mismatch_unconfirmed' && e.data) {
        setPendingConfirm(e.data as RecipientCheckResult);
      }
    }
  });
  // When the report server last accepted a run from this page, so the run-history section can say
  // "started at 09:14" rather than leaving a triggered run with nothing to show for it. Null until
  // one is triggered here — it is news about this visit, not a property of the subscription, and
  // the subscription's own last-run time is already in the panel opposite.
  const [runStartedAt, setRunStartedAt] = useState<number | null>(null);
  const [watchingRuns, setWatchingRuns] = useState(false);

  // Re-armed by each new run rather than only the first: the timestamp changes every time, so a
  // second run started 90 seconds into the first one's window gets its own full window instead of
  // inheriting the 30 seconds left of the previous one.
  useEffect(() => {
    if (runStartedAt === null) return;
    setWatchingRuns(true);
    const timer = setTimeout(() => setWatchingRuns(false), RUN_WATCH_WINDOW_MS);
    return () => clearTimeout(timer);
  }, [runStartedAt]);

  const runNow = useMutation({
    mutationFn: () => api.runNow(id),
    onSuccess: () => {
      setRunStartedAt(Date.now());
      qc.invalidateQueries({ queryKey: ['sub', id] });
      qc.invalidateQueries({ queryKey: ['outbox'] });
      // The prefix, so /history's own query goes with it: both read the same execution log, and a
      // portal that shows a new attempt on one page and not the other is reporting two different
      // report servers. Neither list will have the row yet — see RUN_WATCH_WINDOW_MS.
      qc.invalidateQueries({ queryKey: ['runs'] });
    }
  });
  const setEnabled = useMutation({
    mutationFn: (enabled: boolean) => enabled ? api.enable(id) : api.disable(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sub', id] })
  });
  const del = useMutation({
    mutationFn: () => api.deleteSubscription(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['mine'] }); navigate('/subscriptions'); }
  });

  // Before the early returns: hook order has to stay stable across renders. Nothing is asked of the
  // API until there is a draft to ask about.
  const recipientCheck = useRecipientCheck(
    draft?.recipients ?? { emails: [], groups: [] },
    draft?.parameters ?? [],
    Boolean(draft) && !draft?.isDataDriven
  );

  if (isLoading || !draft) return <div className="placeholder">Loading subscription...</div>;
  if (error) return <div className="error-banner">{(error as Error).message}</div>;

  function handleSave(confirmRecipientMismatch?: string | null) {
    if (!draft) return;
    // Every delivery field goes on the wire, not just the edited ones: a PBIRS update replaces the
    // whole subscription, so anything omitted here is removed from the live subscription.
    save.mutate({
      emailSubject: draft.emailSubject,
      comment: draft.comment,
      renderFormat: draft.renderFormat,
      recipients: draft.recipients,
      schedule: draft.schedule,
      parameters: draft.parameters,
      priority: draft.priority,
      includeReport: draft.includeReport,
      includeLink: draft.includeLink,
      ifMatchEtag: draft.metadata.etagHash,
      confirmRecipientMismatch,
      // Blank is sent as null rather than "": that is what clears the name back to the one the
      // report server generates, and an empty string would be stored as a name that renders as
      // nothing at all.
      displayName: draft.displayName?.trim() || null
    });
  }

  const definitions = draft.parameterDefinitions ?? [];

  // From `data` rather than `draft`: whether a pickup route exists is a fact about the report
  // server, not something the form on screen can change.
  const deliversToPickup = Boolean(data?.pickup && data.pickup.health !== 'None');

  return (
    <>
      <div className="page-header">
        <div>
          {/* The name if it has one, the report's name if not — and the path underneath either
              way, so a named subscription never hides which report it actually runs. Read from
              `data` rather than `draft` so the heading does not rename itself letter by letter as
              somebody types in the Name field below; it changes when the save lands. */}
          <h1>{subscriptionTitle(data ?? draft)}</h1>
          <div className="sub">{draft.reportPath}</div>
        </div>
        <StatusPill status={draft.isEnabled ? draft.lastStatus : 'Disabled'} />
      </div>

      {/* Read from `data`, not `draft`: this describes what is on the report server right now, and
          must not appear to change as somebody types into the form. */}
      <PickupDriftNotice pickup={data?.pickup} />

      {createPickupProblem && (
        <div className="notice notice-warn" style={{ marginBottom: 18 }}>
          <strong>This subscription was created, but its report pickup delivery was not.</strong>
          <div>
            {createPickupProblem.problem
              ?? (createPickupProblem.outcome === 'Unconfigured'
                ? 'Report pickup is not configured in this environment.'
                : 'The pickup delivery could not be set up.')}
            {' '}The email side is in place and will send on schedule. Nothing is being filed to
            pickup until it is switched on in <strong>Recipients and delivery</strong> below, which
            retries exactly what the create attempted.
          </div>
          <button
            className="btn-secondary"
            style={{ marginTop: 10 }}
            onClick={() => setCreatePickupProblem(null)}>
            Dismiss
          </button>
        </div>
      )}

      {reassignedTo && (
        <div className="notice notice-warn" style={{ marginBottom: 18 }}>
          <strong>Updated the portal owner to {reassignedTo}.</strong>
          <div>
            This changed the portal&rsquo;s own ownership record only. It does <strong>not</strong> change who
            the report server sends mail as — see &ldquo;Sends as (Report Server)&rdquo; below. If that
            account is broken, this subscription&rsquo;s mail delivery will keep failing until the report
            server record is fixed there directly.
          </div>
          <button className="btn-secondary" style={{ marginTop: 10 }} onClick={() => setReassignedTo(null)}>
            Dismiss
          </button>
        </div>
      )}

      {draft.isDataDriven && (
        <div className="notice notice-warn" style={{ marginBottom: 18 }}>
          <strong>This subscription is data-driven — you can view it here, but not change it.</strong>
          <div>
            Its recipients and parameter values come from a query rather than a fixed list, so one
            run sends a different report to each recipient. Saving it here would replace that query
            with a single fixed recipient list. Use Report Server Schedule Manager to change it.
          </div>
        </div>
      )}

      {!draft.isDataDriven && draft.uneditableScheduleReason && (
        <div className="notice notice-warn" style={{ marginBottom: 18 }}>
          <strong>This subscription's schedule can't be edited here — you can view it, but not save it.</strong>
          <div>
            The schedule below is a simplified version of the real one: {draft.uneditableScheduleReason}.
            Saving would replace the real schedule with the simplified version, so it is blocked.
            Use Report Server Schedule Manager to change it.
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {/* Run now, Disable and Delete all act on the pair, not just the email half — a paused
            subscription that kept filing to pickup, or a deleted one that kept writing files, would
            be the portal saying one thing while MANDW01 did another. The confirmations say so.
            Clone is the exception and creates nothing until the form is submitted. */}
        <button className="btn-primary" onClick={() => runNow.mutate()} disabled={runNow.isPending || !draft.isEnabled}>
          {runNow.isPending ? 'Running...' : 'Run now'}
        </button>
        <button
          className="btn-secondary"
          onClick={() => setEnabled.mutate(!draft.isEnabled)}
          disabled={setEnabled.isPending}
          title={deliversToPickup
            ? draft.isEnabled
              ? 'Stops the email and the pickup delivery'
              : 'Restarts the email and the pickup delivery'
            : undefined}>
          {setEnabled.isPending ? 'Working...' : draft.isEnabled ? 'Disable' : 'Enable'}
        </button>
        {/* Clone carries everything about this subscription EXCEPT who receives it — see the
            create form, which says so on screen. The source id travels in the query string rather
            than in router state so the half-filled form survives a reload and can be handed to
            somebody else as a link. */}
        <button
          className="btn-secondary"
          onClick={() => navigate(`/subscriptions/new?cloneFrom=${encodeURIComponent(draft.subscriptionId)}`)}
          title="Create another subscription to this report with the same settings, and new recipients">
          Clone
        </button>
        <div style={{ flex: 1 }} />
        <button
          className="btn-danger"
          onClick={() => {
            const name = subscriptionTitle(data ?? draft);
            const message = deliversToPickup
              ? `Delete "${name}"? This cannot be undone. Its report pickup delivery is `
                + 'deleted too — files already collected stay available and downloadable.'
              : `Delete "${name}"? This cannot be undone.`;
            if (confirm(message)) del.mutate();
          }}
          disabled={del.isPending}>
          Delete subscription
        </button>
      </div>

      <div className="detail-layout">
        <div>
          <Section title="Name and email">
            <div className="field">
              <label>Name (optional)</label>
              <input
                className="input"
                value={draft.displayName ?? ''}
                placeholder={draft.reportName}
                onChange={e => setDraft({ ...draft, displayName: e.target.value })}
              />
              <div className="field-hint">
                What this subscription is called in lists and headings — useful when the same report
                is subscribed to several times over for different clients. Leave it blank and the
                report name is used instead. This is the report server's own name for the
                subscription, so Report Server Schedule Manager shows it too.
              </div>
            </div>
            <div className="field">
              <label>Subject</label>
              <input className="input" value={draft.emailSubject} onChange={e => setDraft({ ...draft, emailSubject: e.target.value })} />
              <SubjectPlaceholderHint parameterNames={definitions.map(d => d.name)} />
            </div>
            <div className="field">
              <label>Comment / message body</label>
              <textarea
                className="input"
                rows={4}
                value={draft.comment}
                onChange={e => setDraft({ ...draft, comment: e.target.value })}
                style={AUTOSIZE_TEXTAREA_STYLE}
              />
            </div>
            <div className="field-row">
              <div className="field">
                <label>Render format</label>
                <select className="select" value={draft.renderFormat} onChange={e => setDraft({ ...draft, renderFormat: e.target.value })}>
                  {RENDER_FORMATS.some(f => f.value === draft.renderFormat) ? null : (
                    <option value={draft.renderFormat}>{draft.renderFormat}</option>
                  )}
                  {RENDER_FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Priority</label>
                <select className="select" value={draft.priority} onChange={e => setDraft({ ...draft, priority: e.target.value })}>
                  <option>LOW</option>
                  <option>NORMAL</option>
                  <option>HIGH</option>
                </select>
              </div>
            </div>
            <div className="field">
              <label className="checkbox-field">
                <input
                  type="checkbox"
                  checked={draft.includeReport}
                  onChange={e => setDraft({ ...draft, includeReport: e.target.checked })}
                />
                Attach the report to the email
              </label>
              <label className="checkbox-field">
                <input
                  type="checkbox"
                  checked={draft.includeLink}
                  onChange={e => setDraft({ ...draft, includeLink: e.target.checked })}
                />
                Include a link to the report on the report server
              </label>
            </div>
          </Section>

          <Section title="Report parameters">
            <ParametersEditor
              definitions={definitions}
              values={draft.parameters}
              onChange={parameters => setDraft({ ...draft, parameters })}
              reportPath={draft.reportPath}
            />
          </Section>

          <Section title="Schedule">
            <ScheduleEditor value={draft.schedule} onChange={s => setDraft({ ...draft, schedule: s })} />
            {draft.schedule.humanReadable && (
              <div style={{ marginTop: 10, padding: '10px 14px', background: 'var(--kt-grey-50)', border: '1px dashed var(--kt-grey-300)', borderRadius: 'var(--radius-sm)', fontSize: '.88rem' }}>
                {draft.schedule.humanReadable}
              </div>
            )}
          </Section>

          {/* Recipients and pickup in one section, matching the create form: both answer "where
              does this report go?", and separating them made pickup read as a different feature
              rather than another destination. */}
          <Section title="Recipients and delivery">
            <RecipientsEditor value={draft.recipients} onChange={r => setDraft({ ...draft, recipients: r })} />
            <RecipientMismatchNotice check={recipientCheck} />

            {/* Outside the draft/save cycle on purpose: turning pickup on or off creates or
                deletes a second subscription on the report server, so it must not ride along
                with an unrelated edit to the subject line. The button below says so. */}
            <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--kt-grey-200)' }}>
              <div style={{ fontWeight: 600, color: 'var(--kt-green-dark)', marginBottom: 8 }}>
                Report pickup
              </div>
              <PickupToggle subscriptionId={draft.subscriptionId} producer="Pbirs" pickup={data?.pickup} />
            </div>
          </Section>

          {/* The mismatch refusal has its own dialog, so showing it here as well would report a
              failure for something the page is already asking a question about. */}
          {save.error && !pendingConfirm && (
            <div className="error-banner" style={{ marginTop: 14 }}>
              {(save.error as Error).message.replace(/^API \d+: /, '')}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
            <button className="btn-secondary" onClick={() => data && setDraft(data)}>Reset</button>
            {/* Disabled rather than hidden: a missing button reads as a bug, and the notice at the
                top of the page explains why this one can't be pressed. The API refuses it too — the
                guard lives in SubscriptionService, not here. */}
            <button
              className="btn-primary"
              onClick={() => handleSave()}
              disabled={save.isPending || !!draft.isDataDriven || !!draft.uneditableScheduleReason}
              title={draft.isDataDriven
                ? 'Data-driven subscriptions must be edited in Report Server Schedule Manager'
                : draft.uneditableScheduleReason
                  ? 'This schedule uses a recurrence the editor cannot represent — edit it in Report Server Schedule Manager'
                  : undefined}>
              {save.isPending ? 'Saving...' : 'Save changes'}
            </button>
          </div>

          {/* Below the save row, not above it, because it is not part of the form: everything
              between "Name and email" and Save is a draft waiting to be written, and a section
              wedged in front of the buttons would read as the last thing they save. Nothing here
              is editable and nothing here is affected by an unsaved edit — the runs are the report
              server's own record, and Run now uses the saved subscription, which the copy says. */}
          <RunHistorySection
            reportPath={draft.reportPath}
            isEnabled={draft.isEnabled}
            lastRunUtc={draft.lastRunUtc}
            running={runNow.isPending}
            runError={runNow.isError ? (runNow.error as Error) : null}
            startedAt={runStartedAt}
            watching={watchingRuns}
            onRunNow={() => runNow.mutate()}
            onDismissStarted={() => { setRunStartedAt(null); setWatchingRuns(false); runNow.reset(); }}
          />
        </div>

        <aside>
          <div className="card" style={{ marginBottom: 18, padding: '18px 20px' }}>
            <div className="card-label">Next run</div>
            <div style={{ fontSize: '1.05rem', fontWeight: 600, marginTop: 6, color: 'var(--kt-green-dark)' }}>
              {draft.isEnabled
                ? draft.nextRunUtc ? new Date(draft.nextRunUtc).toLocaleString() : 'Not scheduled'
                : 'Disabled'}
            </div>
            <div style={{ fontSize: '.82rem', color: 'var(--kt-grey-500)', marginTop: 8 }}>
              Last run: {draft.lastRunUtc ? new Date(draft.lastRunUtc).toLocaleString() : 'never'}
            </div>
          </div>
          <div className="card" style={{ marginBottom: 18, padding: '18px 20px' }}>
            <div className="card-label">Last edited</div>
            <div style={{ fontSize: '.95rem', marginTop: 6 }}>
              {draft.metadata.lastEditedUtc ? new Date(draft.metadata.lastEditedUtc).toLocaleString() : '—'}
              {draft.metadata.lastEditedBy && <> by {draft.metadata.lastEditedBy}</>}
            </div>
          </div>
          <div className="card" style={{ padding: '18px 20px' }}>
            <div className="card-label" style={{ marginBottom: 'var(--space-2)' }}>Subscription info</div>
            <div style={{ fontSize: '.88rem', display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 6 }}>
              {/* Two different owners, shown as two different rows on purpose — see
                  ReassignOwnerDialog and SubscriptionSummary.portalOwnerDisplayName. Collapsing
                  them into one "Owner" row would let a portal-only reassignment read as having
                  fixed a mail-delivery failure it cannot touch. */}
              <span style={{ color: 'var(--kt-grey-500)' }}>Portal owner</span>
              <span>
                {draft.metadata.portalOwnerDisplayName ?? (
                  <span style={{ color: 'var(--kt-grey-400)' }}>Not claimed in the portal</span>
                )}
                {me?.isAdmin && (
                  <button
                    className="btn-secondary"
                    style={{ marginLeft: 8, padding: '2px 8px', fontSize: '.78rem' }}
                    onClick={() => setReassignOpen(true)}>
                    Reassign
                  </button>
                )}
              </span>
              <span
                style={{ color: 'var(--kt-grey-500)' }}
                title="The report server's own record: the Windows account it actually sends mail as.">
                Sends as (Report Server)
              </span>
              <span>{draft.metadata.ownerDisplayName}</span>
              <span style={{ color: 'var(--kt-grey-500)' }}>Schedule</span><span>{draft.schedule.humanReadable ?? '—'}</span>
              <span style={{ color: 'var(--kt-grey-500)' }}>Recipients</span>
              <span>{draft.recipients.emails.length + draft.recipients.groups.length}
                {(draft.recipients.cc?.length ?? 0) > 0 && <> (+{draft.recipients.cc!.length} cc)</>}
              </span>
              {/* Stated on the summary panel as well as in the section below, because this is the
                  part somebody glances at to answer "where does this one go?" without reading the
                  form. Same wording as the list's column, from the same helper. */}
              <span style={{ color: 'var(--kt-grey-500)' }}>Delivers to</span>
              <span>{describeDelivery(data?.pickup).label}</span>
              {/* Run totals are only shown when they are real: the report server records no link
                  from a run back to its subscription, so on the live estate they are always zero
                  and "Success rate 0.0%" was being read as a failing subscription. */}
              {draft.metadata.totalRuns > 0 && (
                <>
                  <span style={{ color: 'var(--kt-grey-500)' }}>Total runs</span><span>{draft.metadata.totalRuns}</span>
                  <span style={{ color: 'var(--kt-grey-500)' }}>Success rate</span><span>{(draft.metadata.successRate * 100).toFixed(1)}%</span>
                </>
              )}
            </div>
          </div>
        </aside>
      </div>

      {pendingConfirm && (
        <RecipientConfirmDialog
          check={pendingConfirm}
          busy={save.isPending}
          onConfirm={() => handleSave(pendingConfirm.fingerprint)}
          onCancel={() => { setPendingConfirm(null); save.reset(); }}
        />
      )}

      {reassignOpen && (
        <ReassignOwnerDialog
          title="Reassign subscription owner"
          subject={<strong>{subscriptionTitle(data ?? draft)}</strong>}
          busy={reassign.isPending}
          error={reassign.isError ? (reassign.error as Error).message.replace(/^API \d+: /, '') : null}
          onCancel={() => { setReassignOpen(false); reassign.reset(); }}
          onConfirm={user => reassign.mutate(user)}
        />
      )}
    </>
  );
}

/**
 * `action` rides in the card header rather than the body because .card-header is already a
 * space-between flex row built for exactly this, and a section's own control belongs level with
 * its title rather than as the first thing inside it. Omitting it leaves the header as it was.
 */
function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <div className="card-header"><h3>{title}</h3>{action}</div>
      <div className="card-body">{children}</div>
    </div>
  );
}

/**
 * The runs the report server has attributed to this subscription, newest first, and a control that
 * starts another one.
 *
 * It exists here as well as on /history because answering "did last night's run work, and why not?"
 * meant leaving the subscription you already had open, filtering a 200-row log by hand, and coming
 * back. Deliberately a summary and not a second copy of that page: the newest {@link
 * RUN_HISTORY_TAKE}, three columns, and a link to the full log for anything older.
 *
 * The section owns its query rather than taking rows as a prop so the run-now polling window can
 * live with the thing it is polling; the parent owns the mutation, because Run now at the top of
 * the page is the same action and must not be a second mutation with its own separate idea of
 * whether a run is in flight.
 */
function RunHistorySection({
  reportPath, isEnabled, lastRunUtc, running, runError, startedAt, watching,
  onRunNow, onDismissStarted
}: {
  // No subscriptionId: this section is scoped by report, because the report server's execution log
  // cannot be scoped by subscription. See the query below.
  reportPath: string;
  isEnabled: boolean;
  lastRunUtc?: string | null;
  running: boolean;
  runError: Error | null;
  startedAt: number | null;
  watching: boolean;
  onRunNow: () => void;
  onDismissStarted: () => void;
}) {
  const api = useApi();

  /*
   * Filtered by REPORT, not by subscription — and the copy below says so rather than letting the
   * heading imply otherwise.
   *
   * Filtering by subscription is the exact question, and it returned nothing for every subscription
   * on the estate: the execution log has no subscription column, and the id it was searched for
   * inside the stored parameter string appears in 0 of 19,937 rows. A section that is always empty
   * teaches people the report isn't running. The report is named on every row, so this is the same
   * question answered one level out — wider where a report carries several subscriptions, exact
   * where it carries one, and honest about which.
   */
  const { data, isLoading, error } = useQuery({
    queryKey: ['runs', 'report', reportPath],
    queryFn: () => api.runHistory({ reportPath, take: RUN_HISTORY_TAKE }),
    enabled: Boolean(reportPath),
    // False, not a number, once the window closes: a list that keeps re-reading itself all day is
    // how a portal left open on a wall display becomes the report server's busiest caller.
    refetchInterval: watching ? RUN_WATCH_POLL_MS : false
  });

  const runs = data ?? [];

  /*
   * Only the newest run can carry a message, and only when it failed.
   *
   * ExecutionLogStorage holds no failure text at all — the reader fills `errorMessage` in from
   * dbo.Subscriptions.LastStatus, which is ONE string per subscription describing its most recent
   * attempt, and it stamps that same string onto every failed row it returns. On a page filtered to
   * a single subscription that would print the identical message against five different failures as
   * if each had produced it, and if the latest attempt succeeded it would print a success string
   * ("Mail sent to...") under a red Failed pill. Restricting it to row 0, and only when row 0 is
   * itself Failed, is exactly the case where LastStatus is describing the run it is shown against.
   * The note under the table says why the older failures are bare rather than leaving it a mystery.
   */
  const newest = runs[0];
  const newestFailure = newest?.status === 'Failed' ? newest.errorMessage?.trim() || null : null;
  // Every failure the rule above leaves bare, which is all of them whenever the latest attempt
  // succeeded — the common case for a subscription that failed on Tuesday and has been fine since.
  const unexplainedFailures = runs.some(r => r.status === 'Failed' && !(newestFailure && r.executionId === newest.executionId));

  return (
    <Section
      title="Run history"
      action={
        <button
          className="btn-secondary btn-sm"
          onClick={onRunNow}
          disabled={running || !isEnabled}
          title={isEnabled
            ? 'Starts a new run now, with the settings saved on the report server'
            : 'A disabled subscription cannot be run'}>
          {running ? 'Starting...' : 'Run now'}
        </button>
      }>

      {/* Says what the button does in the one place somebody might expect it to do something else.
          PBIRS has no concept of replaying an execution — a run is a subscription firing, and the
          only thing that can be fired is the subscription as it stands — so a history section with
          a button in it has to rule that reading out rather than leave it available. */}
      <div className="field-hint" style={{ marginTop: 0, marginBottom: 14 }}>
        The most recent runs of <strong>this report</strong> — every subscription on it, plus anyone
        who opened it themselves. The report server's execution log records which report ran but not
        which subscription asked for it, so these cannot be narrowed further; where this report has
        only one subscription, they are all this one's.{' '}
        <strong>Run now</strong> starts a fresh run using the settings currently saved on the report
        server — not the unsaved edits above it, and not a re-send of any run below: the report
        server cannot repeat a past run or the parameters it used.
      </div>

      {runError && (
        <div className="error-banner">
          The report server did not accept the run: {runError.message.replace(/^API \d+: /, '')}
        </div>
      )}

      {startedAt !== null && !runError && (
        <div className="notice" style={{ border: '1px solid var(--kt-grey-200)' }}>
          <strong>Run started at {new Date(startedAt).toLocaleTimeString()}.</strong>
          <div>
            Started, not sent — the report server has accepted the request and says nothing further
            until the report has rendered and the mail has left.{' '}
            {watching
              ? 'This list is re-reading itself every few seconds for a couple of minutes in case the attempt appears in it.'
              : 'It stopped re-reading this list after a couple of minutes; reload the page to look again.'}
            {' '}It will appear as a run of this report — the log does not record which subscription
            started it, so if this report has other subscriptions the newest row is the likeliest
            but not a certain match.
          </div>
          <button className="btn-secondary btn-sm" style={{ marginTop: 10 }} onClick={onDismissStarted}>
            Dismiss
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="placeholder">Loading run history...</div>
      ) : error ? (
        // Its own banner inside the section, not the page-level one: the subscription loaded fine
        // and every other section on it is still usable, so a failed history read must not take
        // the page down with it.
        <div className="error-banner" style={{ marginBottom: 0 }}>
          Could not read this subscription's run history: {(error as Error).message.replace(/^API \d+: /, '')}
        </div>
      ) : runs.length === 0 ? (
        <EmptyRunHistory lastRunUtc={lastRunUtc} />
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th>When</th>
                {/* Carries its weight now the list is report-wide: a run somebody kicked off by
                    opening the report is not this subscription's overnight delivery, and without
                    this column the two are indistinguishable. */}
                <th>Started by</th>
                <th>Outcome</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {runs.map(r => (
                // cursor: default because these rows do nothing when clicked — .data-table gives
                // every row a pointer, which is honest on My Subscriptions and /history where a
                // row opens something, and a promise this table cannot keep.
                <tr key={r.executionId} style={{ cursor: 'default' }}>
                  {/* data-label="" rather than "When": below 640px the table becomes one card per
                      row, and this cell is what the row IS — the timestamp is the run's identity
                      here in the way the report name is on /history, so it takes the card's
                      heading treatment instead of being captioned like an ordinary field. */}
                  <td data-label="" style={{ whiteSpace: 'nowrap' }}>
                    {new Date(r.startedUtc).toLocaleString()}
                  </td>
                  <td data-label="Started by" style={{ fontSize: '.85rem' }}>
                    {r.source === 'Subscription' ? 'A subscription' : r.source === 'Interactive' ? 'Opened by hand' : r.source ?? '—'}
                    {r.ownerUserName && (
                      <div style={{ fontSize: '.75rem', color: 'var(--kt-grey-500)', marginTop: 2 }}>
                        {r.ownerUserName}
                        {r.ownerSource === 'Report' && ' (the report’s only owner)'}
                      </div>
                    )}
                  </td>
                  <td data-label="Outcome">
                    <StatusPill status={r.status} />
                    {/* Full text, wrapped, in the row — the same treatment /history gives it. PBIRS
                        failure text is long and the useful part is rarely at the front, so nothing
                        here truncates it or hides it behind another click. */}
                    {r.executionId === newest?.executionId && newestFailure && (
                      <div style={{ fontSize: '.82rem', color: 'var(--kt-red)', marginTop: 4 }}>
                        {newestFailure}
                      </div>
                    )}
                  </td>
                  <td data-label="Duration" style={{ whiteSpace: 'nowrap' }}>
                    {(r.durationMs / 1000).toFixed(1)}s
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {unexplainedFailures && (
            <div className="run-detail-note">
              {newestFailure
                ? 'Only the newest failure above carries a message.'
                : 'None of the failures above carry a message.'}
              {' '}The execution log stores no failure text of its own: the only wording the report
              server keeps is the subscription's current status, which describes its most recent
              attempt — so a failure that is not that attempt has none, and there is nowhere left to
              recover it from.
            </div>
          )}

          <div style={{
            marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8,
            alignItems: 'baseline', justifyContent: 'space-between'
          }}>
            <span className="field-hint" style={{ marginTop: 0 }}>
              {runs.length === RUN_HISTORY_TAKE
                ? `The ${RUN_HISTORY_TAKE} most recent runs of this report.`
                : `${runs.length} run${runs.length === 1 ? '' : 's'} recorded against this report.`}
            </span>
            {/* By report, matching the table above. The old link carried subscriptionId, which
                filtered on a value no row holds — so it opened a page guaranteed to be empty. */}
            <Link
              to={`/history?reportPath=${encodeURIComponent(reportPath)}`}
              title="This report's executions for the last 30 days">
              Open the full run history
            </Link>
          </div>
        </>
      )}
    </Section>
  );
}

/**
 * The empty state, which on the live estate is the COMMON one and must not read as "this has never
 * run".
 *
 * A run is only tied back to its subscription when the subscription's id was written into the
 * execution-log row's parameters, and nothing on MANDW01 writes it — which is the same fact that
 * keeps SubscriptionMetadata.TotalRuns at 0 and is why Subscription info hides run totals rather
 * than showing "Success rate 0.0%" against a subscription that has never missed a night. So the
 * copy has to name the recording gap, and hand back the one number that IS reliable: the
 * subscription's own last-run time, which comes from its record rather than from the log.
 */
function EmptyRunHistory({ lastRunUtc }: { lastRunUtc?: string | null }) {
  return (
    <div className="placeholder" style={{ textAlign: 'left', padding: '4px 0 0' }}>
      <strong>No runs recorded against this report.</strong>
      <div style={{ marginTop: 6 }}>
        Still not quite the same as never having run: the execution log is trimmed on a retention
        schedule, so a report that runs monthly can age out of it between runs.{' '}
        {lastRunUtc
          ? `This subscription's own record says it last ran on ${new Date(lastRunUtc).toLocaleString()}.`
          : 'This subscription carries no last-run time of its own either, so it may genuinely never have run.'}
      </div>
    </div>
  );
}
